import { useEffect } from "react";
import { unlockAudio } from "@/lib/earcons.js";
import { primeSpeech } from "@/lib/speech.js";

/**
 * useAudioUnlock — desbloquea el audio con el primer gesto del usuario en la app.
 *
 * Por qué existe: la política de autoplay concede el permiso POR DOCUMENTO, y
 * hasta ahora el único gesto que desbloqueaba era el switch (o el botón
 * "Probar") de la tarjeta de ajustes. Con la voz ya activada en localStorage,
 * al reabrir la app nadie volvía a desbloquear nada: el AudioContext quedaba
 * `suspended` —`playEarcon` sale sin sonar— y `speechSynthesis.speak()` en
 * móvil se descarta en silencio. Resultado: la voz andaba exactamente una vez,
 * justo después de encenderla, y nunca más. En escritorio se disimulaba porque
 * Chrome concede autoplay por historial de interacción con el sitio; en móvil
 * la política es estricta y no perdona.
 *
 * Ahora sirve CUALQUIER gesto en la app — un tap en el mapa alcanza.
 *
 * El listener queda vivo en vez de usar `once`: iOS vuelve a suspender el
 * AudioContext cada vez que la app pasa a segundo plano, así que el siguiente
 * toque tiene que poder revivirlo. `unlockAudio()` es un chequeo de estado
 * barato cuando ya está corriendo. El cebado del sintetizador, en cambio, corre
 * una sola vez: encolar locuciones mudas en cada toque metería pausas en medio
 * de una alerta real.
 *
 * @param {boolean} enabled — si la locución por voz está activada
 */
export function useAudioUnlock(enabled) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    let primed = false;
    const onGesture = () => {
      // Sin await: esto corre dentro del gesto y no debe demorar el handler.
      // El desbloqueo del AudioContext ya quedó pedido dentro de la activación
      // del usuario, que es lo único que exige la política.
      unlockAudio();
      if (!primed) primed = primeSpeech();
    };

    // `pointerdown` y no `click`: en móvil llega antes y cubre el scroll y los
    // gestos sobre el mapa, que muchas veces nunca terminan en un click.
    const opciones = { capture: true, passive: true };
    window.addEventListener("pointerdown", onGesture, opciones);
    window.addEventListener("keydown", onGesture, opciones);
    window.addEventListener("touchend", onGesture, opciones);

    return () => {
      window.removeEventListener("pointerdown", onGesture, opciones);
      window.removeEventListener("keydown", onGesture, opciones);
      window.removeEventListener("touchend", onGesture, opciones);
    };
  }, [enabled]);
}
